import { html, render } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { ComponentId, ComponentStore, dom, SplitContainer } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { NodeModel, NodeSource, Tree, TreeEvent } from "@omegagrid/tree";
import { CodeDiffEditor } from "@omegagrid/code";
import rp, { AppComponent, BrowserAdapter } from "@reporting/rp-app-host";
import { EtlMergeEditorAdapter, AccountingTreeAdapter } from "../adapters/etlMergeEditorAdapter";
import { MergeDiffItem, MergeKind, MergeStatus } from "@reporting/rp-types/etl";
import { SortMode, buildFolderTree, makeSiblingComparator, walkTree } from "../folderTree";
import { style } from "./mergeEditor.style";

const STATUS_COLOR: Record<MergeStatus, string> = {
	M: '#0094ff', // modified
	C: '#13a01a', // created (only in source)
	D: '#d1242f', // deleted (only in target)
};
const STATUS_NAME: Record<MergeStatus, string> = {
	M: 'Modify',
	C: 'Create',
	D: 'Delete',
};
const DIFF_OPTIONS = { readOnly: true, tabSize: 4, insertSpaces: false };

@customElement('rp-mf-etl-merge-editor')
export class MergeEditor extends AppComponent {

	static styles = [style];

	@property({ type: Object })
	adapter: EtlMergeEditorAdapter = new EtlMergeEditorAdapter();

	/** Which ETL objects this editor merges */
	@property({ type: String })
	kind: MergeKind = 'thread';

	@query('og-splitcontainer')
	splitContainer: SplitContainer;

	@state() private sourceA: number = null;
	@state() private diffItems: MergeDiffItem[] = [];
	@state() private selectedCount = 0;
	@state() private hasSelection = false;

	private sourceAdapter = new AccountingTreeAdapter();
	private threadTypes = new Map<string, string>();
	private itemByKey = new Map<string, MergeDiffItem>();
	private resizeObserver: ResizeObserver;

	private componentStore = new ComponentStore<Tree | CodeDiffEditor>((index: number) => {
		if (index === 0) {
			return dom.createElement<Tree>('og-tree', {
				disableAutoload: true,
				listeners: [
					['select', this._onTreeSelect],
					['change', this._onTreeChange],
				],
			});
		}
		return dom.createElement<CodeDiffEditor>('og-code-diff', {
			positionOffset: { t: 1, l: 1, r: -2, b: -2 },
			options: DIFF_OPTIONS,
			language: 'sql',
		});
	});

	private get tree() { return this.componentStore.items.get(0) as Tree; }
	private get diff() { return this.componentStore.items.get(1) as CodeDiffEditor; }
	get targetA() { return rp.accountingProvider.a; }

	connectedCallback() {
		super.connectedCallback();
		if (this.kind === 'thread') this.loadThreadTypes();
	}

	firstUpdated() {
		this.resizeObserver = new ResizeObserver(() => this.splitContainer?.layout());
		this.resizeObserver.observe(this);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.resizeObserver?.disconnect();
	}

	layout() {
		this.splitContainer?.layout();
	}

	async loadThreadTypes() {
		const types = await this.adapter.getThreadTypes();
		this.threadTypes = new Map((types ?? []).map(t => [t.k, t.v]));
	}

	async refreshDiff() {
		if (!this.sourceA) return;
		this.showLoader();
		try {
			this.diffItems = (await this.adapter.getDiff(this.sourceA, this.kind)) ?? [];
		} catch (e) {
			this.diffItems = [];
		}
		this.itemByKey = new Map(this.diffItems.map(i => [i.key, i]));
		this.hasSelection = false;
		this.selectedCount = 0;
		await this.updateComplete;
		this.tree?.setSourceData(this.buildNodes(), {
			selectMode: 'multiple',
			checkboxes: true,
			globalLevels: false,
			expanders: true,
			folderOptions: BrowserAdapter.folderOptions,
			nodeRenderer: this.nodeRenderer,
			afterRenderer: this.afterRenderer,
		});
		this.hideLoader();
	}

	private buildNodes(): NodeSource[] {
		const nodes: NodeSource[] = [];

		if (this.kind === 'file') {
			this.pushTree(nodes, this.diffItems, 'description', 'g:file', 0);
			return nodes;
		}

		const byType = new Map<string, MergeDiffItem[]>();
		for (const item of this.diffItems) {
			const type = item.type ?? '';
			if (!byType.has(type)) byType.set(type, []);
			byType.get(type).push(item);
		}

		// Order type groups like the browser (known thread-type order), unknown types last.
		const types = [...this.threadTypes.keys()].filter(t => byType.has(t));
		for (const t of byType.keys()) if (!types.includes(t)) types.push(t);

		const mode = (rp.settings.get('etl.threads.sorting') as SortMode) || 'description/ord_num';
		for (const type of types) {
			nodes.push({ k: `g:thread:${type}`, v: this.threadTypes.get(type) ?? type ?? msg('Unknown'), l: 0, ch: false, collapsed: true, folder: BrowserAdapter.folderOptions });
			this.pushTree(nodes, byType.get(type) ?? [], mode, `g:thread:${type}`, 1);
		}
		return nodes;
	}

	/** Expands items into a virtual folder tree (folder + leaf nodes) and appends them to `nodes`. */
	private pushTree(nodes: NodeSource[], items: MergeDiffItem[], mode: SortMode, keyPrefix: string, baseLevel: number) {
		const compare = makeSiblingComparator<MergeDiffItem>(mode, i => i.ord_num || 0);
		const root = buildFolderTree(items, i => i.name, i => i.ord_num || 0);
		walkTree(root, compare, {
			folder: (folder, level) => nodes.push({
				k: `${keyPrefix}:${folder.pathChunks.join('/')}`,
				v: folder.name,
				l: level,
				ch: false,
				collapsed: true,
				folder: BrowserAdapter.folderOptions,
				props: { dir: true }
			}),
			file: (leaf, _parent, level) => nodes.push({
				k: leaf.item.key,
				v: leaf.filename,
				l: level,
				ch: !leaf.item.ambiguous,
				props: { item: leaf.item }
			})
		}, baseLevel);
	}

	private nodeRenderer = (div: HTMLDivElement, node: NodeModel) => {
		const item = node.properties?.item as MergeDiffItem;
		if (!item) {
			div.textContent = node.value;
			return;
		}
		render(html`
			${item.ambiguous ? html`<og-icon icon="triangle-exclamation" color="orange" title="${msg('Duplicate name')}"></og-icon>` : ''}
			<span style="display:inline-block; width:14px; font-weight:bold; color:${STATUS_COLOR[item.status]}" title="${msg(STATUS_NAME[item.status])}">${item.status}</span>
			<span style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap">${node.value}</span>
		`, div);
	}

	private afterRenderer = (div: HTMLDivElement, node: NodeModel) => {
		const item = node.properties?.item as MergeDiffItem;
		div.style.borderLeft = item ? `3px solid ${STATUS_COLOR[item.status]}` : 'none';
	}

	private _onSourceSelect = (e: TreeEvent) => {
		const a = parseInt(e.node?.key ?? '');
		if (!a || a === this.sourceA) return;
		this.sourceA = a;
		this.refreshDiff();
	}

	private _onTreeSelect = async (e: TreeEvent) => {
		const item = e.node?.properties?.item as MergeDiffItem;
		if (item) await this.showDiff(item);
	}

	private _onTreeChange = () => {
		const selected = this.getSelectedItems();
		this.selectedCount = selected.length;
		this.hasSelection = selected.length > 0;
	}

	private async showDiff(item: MergeDiffItem) {
		if (!this.diff) return;
		const [source, target] = await Promise.all([
			item.source_id ? this.adapter.getContent(this.sourceA, item.kind, item.source_id) : Promise.resolve(''),
			item.target_id ? this.adapter.getContent(this.targetA, item.kind, item.target_id) : Promise.resolve(''),
		]);
		this.diff.language = item.kind === 'file' ? 'json' : 'sql';
		this.diff.originalContent = target ?? '';
		this.diff.modifiedContent = source ?? '';
		this.diff.layout();
	}

	private getSelectedItems(): MergeDiffItem[] {
		if (!this.tree?.selection) return [];
		return Array.from(this.tree.selection.items.keys())
			.map(k => this.itemByKey.get(String(k)))
			.filter((i): i is MergeDiffItem => !!i && !i.ambiguous);
	}

	async merge() {
		const items = this.getSelectedItems();
		if (!items.length) return;

		const deletes = items.filter(i => i.status === 'D');
		if (deletes.length) {
			const cmd = await rp.host.openMenu({
				content: 'yes_no',
				text: `${msg('This will delete {n} item(s) in the current accounting. Continue?', { data: { n: deletes.length } })}`,
			});
			if (cmd?.id !== 'Y') return;
		}

		this.showLoader();
		try {
			await this.adapter.apply(this.sourceA, items);
		} finally {
			this.hideLoader();
		}
		await this.refreshDiff();
		this.dispatchEvent(new CustomEvent('merge', { bubbles: true, composed: true }));
	}

	render = () => html`
		<div class="controls">
			<span class="label">${msg('Source')}:</span>
			<og-tree-dropdown
				color="gray"
				dynamicWidth
				.placeholder="${msg('Select accounting')}"
				.adapter="${this.sourceAdapter}"
				@change="${this._onSourceSelect}">
			</og-tree-dropdown>

			<og-button
				icon="arrows-rotate"
				color="gray"
				style="min-width: 25px"
				title="${msg('Refresh')}"
				?disabled="${!this.sourceA}"
				@click="${() => this.refreshDiff()}">
			</og-button>

			<og-button
				color="green"
				icon="code-merge"
				?disabled="${!this.hasSelection}"
				@click="${() => this.merge()}">
				<div slot="content">${msg('Merge')}${this.selectedCount ? ` (${this.selectedCount})` : ''}</div>
			</og-button>
		</div>

		<og-splitcontainer
			orientation="horizontal"
			gap="6"
			.createComponent="${(id: ComponentId) => this.componentStore.get(id)}"
			.items="${[{ size: 340 }, { size: null }]}">
		</og-splitcontainer>
	`;
}
